import React, { memo, useEffect, useState } from 'react';
import cn from 'classnames';
import { useFormContext } from 'react-hook-form';
import { Icon, IconButton } from 'components';
import { Row, Col } from 'components/layout';
import { Input } from 'components/form';
import ImageUpload from 'components/ImageUpload';
import BannerUpload from 'components/BannerUpload';
import Dropdown from 'components/Dropdown';
import Gamification, { GamificationProps } from 'pages/company/Gamification';
import { UniversityResponse } from 'lib/models/university';
import {
  StudentSocietyRequest,
  StudentSocietyResponse,
} from 'lib/models/student-society';
import { Option } from 'lib/models/option';
import { StatsRequest, StatsAttributes } from 'lib/models/stats';
import SectionWrapper from '../SectionWrapper';
import styles from './SocietyProfileDetails.module.scss';

export interface SocietyProfileDetailsProps extends GamificationProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  /** name */
  name: string;
  /** description */
  description: string;
  /** short description */
  short_description: string;
  /** address */
  university: UniversityResponse;
  /** icon_url */
  icon_url: string;
  /** banner_url */
  banner_url: string;
  /** instagram url */
  instagram_url: string;
  /** facebook url */
  facebook_url: string;
  /** linkedin url */
  linkedin_url: string;
  /** website url */
  website: string;
  /** student union url */
  student_union_url: string;
  /**university list */
  universities: Array<Option>;
  address: string;
  /** On society profile update click handler */
  onUpdate: (
    data: StudentSocietyRequest
  ) => Promise<StudentSocietyResponse | undefined>;
  /** On society profile remove click handler */
  onRemoveStats: (value: string) => Promise<void>;
  onAddStats: (data: StatsRequest) => Promise<void>;
  societyStats: Array<StatsAttributes>;
  onDeleteCoverPhoto: () => Promise<void>;
  showProgressBanner: boolean;
}

export const SocietyProfileDetails: React.FC<SocietyProfileDetailsProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    universities,
    progressDetails,
    profileCompletePercent,
    nonCompletedSection,
    handleProfileCompletionButton,
    handleNextButton,
    showNextButton,
    showProgressBanner,
  }: SocietyProfileDetailsProps) => {
    const contentClassNames = cn(
      {
        [styles.SocietyProfileDetails]: true,
      },
      className
    );
    const [showProgressBannerState, setShowProgressBannerState] =
      React.useState<boolean>(showProgressBanner);

    useEffect(() => {
      if (showProgressBannerState === false)
        setShowProgressBannerState(showProgressBanner);
    }, [showProgressBanner]);

    const {
      register,
      watch,
      setValue,
      formState: { errors },
    } = useFormContext(); // retrieve all hook methods
    const bannerImageFiles = watch('banner');
    const icon = watch('logo');

    useEffect(() => {
      register('logo');
      register('banner');
    }, [register]);

    const [showLogoFileSizeError, setShowLogoFileSizeError] =
      useState<boolean>(false);

    const onChangeLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      const files: FileList = target.files as FileList;
      if (files[0].size / 1000 < 5001) {
        setShowLogoFileSizeError(false);
        setValue('logo', files, { shouldDirty: true });
      } else {
        setShowLogoFileSizeError(true);
      }
    };

    const [showBannerFileSizeError, setShowBannerFileSizeError] =
      useState<boolean>(false);

    const onChangeBanner = (e: React.ChangeEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      const files: FileList = target.files as FileList;
      if (files[0].size / 1000 < 5001) {
        setShowBannerFileSizeError(false);
        setValue('banner', files, { shouldDirty: true });
      } else {
        setShowBannerFileSizeError(true);
      }
    };
    const logoImage =
      typeof icon === 'object' && icon?.length > 0
        ? URL.createObjectURL(icon[0])
        : typeof icon === 'string'
        ? icon
        : '';

    const bannerImage =
      typeof bannerImageFiles === 'object' && bannerImageFiles?.length > 0
        ? URL.createObjectURL(bannerImageFiles[0])
        : bannerImageFiles && typeof bannerImageFiles === 'string'
        ? bannerImageFiles
        : '';

    const defaultImage = '/images/upload-image-2.png';
    const availableImage = bannerImage || defaultImage;
    const isDefaultImage = availableImage === defaultImage;
    const defaultLogoImage = 'upload-photo.png';
    const universityId = watch('university_id');

    const getGamificationProps = () => ({
      progressDetails: progressDetails,
      nonCompletedSection: nonCompletedSection,
      profileCompletePercent: profileCompletePercent,
      showProgressBanner: false,
      handleNextButton: handleNextButton,
      handleProfileCompletionButton: handleProfileCompletionButton,
      showNextButton: showNextButton,
    });

    return (
      <div
        className={contentClassNames}
        style={style}
        id={`section_${progressDetails?.id}`}
      >
        {showProgressBannerState && (
          <Gamification
            profileCompletePercent={profileCompletePercent}
            nonCompletedSection={nonCompletedSection}
            progressDetails={progressDetails}
            handleProfileCompletionButton={() => {
              setShowProgressBannerState(false);
              handleProfileCompletionButton();
            }}
            handleNextButton={() => {
              setShowProgressBannerState(false);
              handleNextButton && handleNextButton();
            }}
            showNextButton={showNextButton}
          />
        )}
        <div
          className={cn(styles.bannerMain, {
            [styles.bannerMainDefault]: isDefaultImage,
          })}
        >
          <img
            src={availableImage}
            className={cn({
              [styles.banner]: !isDefaultImage,
              [styles.bannerDefault]: isDefaultImage,
            })}
          />
          {isDefaultImage ? (
            <p className={styles.bannerMainTitle}>Cover Photo</p>
          ) : null}

          <div
            className={cn(styles.bannerWrapper, {
              [styles.bannerWrapperDefault]: isDefaultImage,
            })}
          >
            <BannerUpload
              image={availableImage}
              color="white"
              buttonClassName={styles.bannerUploadButton}
              className={cn({ [styles.bannerUpload]: !isDefaultImage })}
              alt={'Society banner image'}
              title={bannerImage ? 'Change Cover photo' : 'Add Cover photo'}
              onChange={onChangeBanner}
            />
            {bannerImage && (
              <IconButton
                size="small"
                iconName="trash"
                className={styles.trashIcon}
                onClick={() => {
                  setValue('banner', '', { shouldDirty: true });
                }}
                type="button"
              />
            )}
          </div>
          {showBannerFileSizeError ? (
            <div
              className={cn(styles['error-container'], {
                [styles['has-banner-image']]: bannerImage,
              })}
            >
              <Icon iconName={'info'}></Icon>
              <span>Please upload an image with a size of less than 5 MB</span>
            </div>
          ) : (
            <></>
          )}
        </div>
        <div className={styles.SocietyProfileDetailsForm}>
          <SectionWrapper
            title="Basic Info"
            description="Add your basic info here so that students can identify your
              society with ease."
            {...getGamificationProps()}
          >
            <Row
              className={styles.SocietyProfileDetailsLogo}
              justify="space-between"
            >
              <Col>
                <ImageUpload
                  image={logoImage || defaultLogoImage}
                  alt={'Society profile image'}
                  onChange={onChangeLogo}
                  className={styles.SocietyProfileDetailsLogoImage}
                  label={logoImage ? 'Change logo' : 'Add logo'}
                  imageSrc={logoImage ? undefined : 'aws'}
                />
              </Col>
              {showLogoFileSizeError ? (
                <div className={styles['logo-error-container']}>
                  <Icon iconName={'info'}></Icon>
                  <span>
                    Please upload an image with a size of less than 5 MB
                  </span>
                </div>
              ) : (
                <></>
              )}
            </Row>
            <Row className={styles.SocietyProfileDetailsFormRow}>
              <Col span={24}>
                <Input
                  label="Society Name"
                  {...register('name', {
                    required: 'Name is required',
                  })}
                  isValid={!errors.name}
                  helperText={errors.name?.message}
                  labelClassName={styles.labelField}
                  inputClassName={styles.inputField}
                  maxLength={120}
                />
              </Col>
              <Col
                span={24}
                className={styles.SocietyProfileDetailsFormUniveristyField}
              >
                <Dropdown
                  title="Select University"
                  label="University"
                  {...register('university_id', {
                    required: 'University is required',
                  })}
                  placeholder="Search your university"
                  className={styles.UniversityList}
                  labelClassName={styles.label}
                  options={universities}
                  value={universityId}
                  onChange={(value: string) => setValue('university', value)}
                  alignment="right"
                  disabled
                  hideChevronIconOnDisabled
                />
              </Col>
            </Row>
          </SectionWrapper>
        </div>
      </div>
    );
  }
);

SocietyProfileDetails.displayName = 'SocietyProfileDetails';
